import { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL, uid, fmt, calcPricing } from '../utils';
import { TermsModal } from './TermsModal';
import { useLang } from '../i18n';

let _stripeInstance = null;
let _stripeLoading = null;

function WhatsIncluded() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="whats-included" style={{ margin: '0 0 12px' }}>
      <button
        data-testid="whats-included-toggle"
        onClick={() => setOpen(p => !p)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '12px 14px', background: '#FAFAF8', border: '1px solid #e8e4df',
          borderRadius: open ? '8px 8px 0 0' : 8, cursor: 'pointer', fontFamily: 'Jost, sans-serif',
          fontSize: 13, fontWeight: 600, color: 'var(--bark, #1C1814)',
        }}
      >
        <span>{t('dayPassTitle')}</span>
        <span style={{ fontSize: 11, color: '#8B7E74' }}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div data-testid="whats-included-content" style={{
          padding: '12px 16px', background: '#FAFAF8', border: '1px solid #e8e4df',
          borderTop: 'none', borderRadius: '0 0 8px 8px', fontSize: 12, lineHeight: 1.8,
          color: 'var(--bark)', fontFamily: 'Jost, sans-serif',
        }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{t('thermalExperience')}</div>
          <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
            <li>{t('incThermal')}</li>
            <li>{t('incSauna')}</li>
            <li>{t('incTea')}</li>
            <li>{t('incGym')}</li>
            <li>{t('incTowels')}</li>
          </ul>
          <div style={{ fontSize: 11, color: '#8B7E74', fontStyle: 'italic' }}>
            {t('incNote')}
          </div>
        </div>
      )}
    </div>
  );
}

function getStripe(publishableKey) {
  if (_stripeInstance) return Promise.resolve(_stripeInstance);
  if (_stripeLoading) return _stripeLoading;
  _stripeLoading = new Promise((resolve) => {
    const init = () => { _stripeInstance = window.Stripe(publishableKey); resolve(_stripeInstance); };
    if (window.Stripe) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.onload = init;
    document.head.appendChild(s);
  });
  return _stripeLoading;
}

export function CheckoutModal({ slot, guests, config, displayRemaining, isPopular, urgencyConfig = {}, onClose, onComplete, trackEvent }) {
  const { t } = useLang();
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('form');
  const [emailStatus, setEmailStatus] = useState('idle');
  const [bookingRef, setBookingRef] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [paying, setPaying] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [bookingToken, setBookingToken] = useState('');
  const [termsError, setTermsError] = useState('');

  const [stripeObj, setStripeObj] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const paymentRef = useRef(null);

  const base = calcPricing(slot.price, guests, config.taxRate, config.serviceFeeRate);
  let promoDiscount = 0;
  if (appliedPromo) {
    promoDiscount = appliedPromo.type === 'percent' ? base.subtotal * (appliedPromo.discount / 100) : appliedPromo.discount;
  }
  const finalTotal = base.total - promoDiscount;
  const amountCents = Math.round(finalTotal * 100);

  useEffect(() => {
    if (!config.stripePublishableKey) return;
    getStripe(config.stripePublishableKey).then((s) => setStripeObj(s));
  }, [config.stripePublishableKey]);

  useEffect(() => {
    if (!stripeObj || !paymentRef.current || elements) return;
    const elems = stripeObj.elements({
      mode: 'payment',
      amount: amountCents > 0 ? amountCents : 100,
      currency: 'usd',
      paymentMethodTypes: ['card'],
      appearance: { theme: 'stripe' },
    });
    const pe = elems.create('payment');
    pe.mount(paymentRef.current);
    pe.on('ready', () => setPaymentReady(true));
    setElements(elems);
  }, [stripeObj, amountCents, elements]);

  useEffect(() => {
    if (!elements || amountCents <= 0) return;
    elements.update({ amount: amountCents });
  }, [elements, amountCents]);

  const applyPromo = useCallback(async () => {
    const code = promoInput.toUpperCase().trim();
    if (!code) return;
    setPromoError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, email: email.trim() }),
      });
      const data = await res.json();
      if (data.valid && data.promo) {
        setAppliedPromo(data.promo);
        setPromoError('');
      } else {
        setAppliedPromo(null);
        setPromoError(data.error || 'Invalid promo code');
      }
    } catch {
      setAppliedPromo(null);
      setPromoError('Could not validate promo code');
    }
  }, [promoInput, email]);

  const handlePay = useCallback(async () => {
    if (!name.trim() || !email.trim() || !termsAgreed) {
      setAttempted(true);
      if (!termsAgreed) setTermsError('Please agree to the Terms & Conditions to continue');
      return;
    }
    if (!stripeObj || !elements || !paymentReady) return;
    setAttempted(false);
    setTermsError('');
    setPaying(true);
    setBookingError('');
    const ref = 'LR-' + uid(7);
    setBookingRef(ref);

    // Step 1: Validate card form
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setPaying(false);
      setBookingError(submitError.message);
      return;
    }

    // Step 2: Create PaymentIntent (NO booking in DB yet)
    let piData;
    try {
      const piRes = await fetch(`${BACKEND_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: amountCents,
          booking_ref: ref,
          customer_email: email.trim(),
          customer_name: name.trim(),
          description: `Las Ruinas Day Pass - ${slot.day}, ${slot.date} ${slot.time}`,
        }),
      });
      piData = await piRes.json();
      if (!piData.client_secret) {
        setPaying(false);
        setBookingError(piData.detail || t('failedPayment'));
        return;
      }
    } catch (e) {
      setPaying(false);
      setBookingError(t('networkError'));
      return;
    }

    // Step 3: Charge the card via Stripe
    const { error, paymentIntent } = await stripeObj.confirmPayment({
      elements,
      clientSecret: piData.client_secret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      // Payment failed — NO booking was created, customer can retry
      setPaying(false);
      setBookingError(error.message);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Step 4: Payment succeeded — NOW create booking in MongoDB with paymentStatus: "paid"
      const booking = {
        slotId: slot.id,
        guests,
        name: name.trim(),
        email: email.trim(),
        phone: phone.replace(/\D/g, '') || null,
        total: Math.round(finalTotal * 100) / 100,
        ref,
        method: 'card',
        date: new Date().toISOString(),
        promoCode: appliedPromo ? appliedPromo.code : null,
        promoDiscount: Math.round(promoDiscount * 100) / 100,
        termsAgreed: true,
        termsAgreedAt: new Date().toISOString(),
        termsVersion: '1.0',
        termsText: 'I agree to the Terms & Conditions of Las Ruinas Bathhouse including the cancellation and refund policy.',
        paymentStatus: 'paid',
        paymentIntentId: paymentIntent.id,
      };

      try {
        const bookRes = await fetch(`${BACKEND_URL}/api/book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(booking),
        });
        if (!bookRes.ok) {
          const data = await bookRes.json();
          setPaying(false);
          setBookingError(data.detail || t('slotNoLongerAvailable'));
          return;
        }
        const bookData = await bookRes.json();
        if (bookData.rescheduleToken) setBookingToken(bookData.rescheduleToken);
      } catch (e) {
        setPaying(false);
        setBookingError(t('networkErrorBooking') + ref);
        return;
      }

      // Step 5: Booking saved. Show success.
      try {
        await onComplete();
      } catch (e) {}
      if (trackEvent) trackEvent('purchase', { value: finalTotal, ref });
      setStep('success');

      // Step 6: Send confirmation email (non-blocking)
      setEmailStatus('sending');
      try {
        const res = await fetch(`${BACKEND_URL}/api/send-booking-confirmation/${ref}`, { method: 'POST' });
        const data = await res.json();
        setEmailStatus(data.success ? 'sent' : 'failed');
      } catch (e) {
        setEmailStatus('failed');
      }
    }
  }, [stripeObj, elements, paymentReady, amountCents, slot, guests, name, email, phone, finalTotal, appliedPromo, promoDiscount, config, onComplete, termsAgreed]);

  if (step === 'success') {
    const confirmationUrl = bookingToken ? `/booking/${bookingRef}/${bookingToken}` : '';
    return (
      <div className="lr-overlay" data-testid="checkout-success">
        <div className="lr-modal">
          <button className="close-btn" data-testid="close-success" onClick={onClose}>&times;</button>
          <div className="lr-success">
            <div className="sparkle">{'\u2728'}</div>
            <h3>{t('youreConfirmed')}</h3>
            <p style={{color:'var(--bark)',fontSize:13,marginBottom:12}}>{t('dayPassBooked')}</p>
            <div className="detail">
              {slot.day}, {slot.date}<br/>
              {slot.time} &middot; {guests} {guests > 1 ? t('guests') : t('guest')}<br/>
              {fmt(finalTotal)}
            </div>
            <div className="ref-badge" data-testid="booking-ref">{bookingRef}</div>
            <div className={`lr-email-status ${emailStatus}`} data-testid="email-status">
              {emailStatus === 'sending' && <><span className="spinner"></span> {t('sendingEmail')}</>}
              {emailStatus === 'sent' && <>{'\u2713'} {t('emailSent')}</>}
              {emailStatus === 'failed' && <>{'\u26A0'} {t('emailFailed')}</>}
              {emailStatus === 'idle' && null}
            </div>
            {confirmationUrl && (
              <a href={confirmationUrl} className="lr-view-booking-btn" data-testid="view-my-booking-btn">
                {t('viewMyBooking')}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
  <>
    <div className="lr-overlay" data-testid="checkout-modal">
      <div className="lr-modal">
        <button className="close-btn" data-testid="close-checkout" onClick={onClose} disabled={paying}>&times;</button>
        <h3>{t('checkout')}</h3>
        {(() => {
          const showCheckoutUrgency = urgencyConfig.checkoutUrgencyEnabled !== false;
          const lowThresh = urgencyConfig.urgencyLowThreshold ?? 4;
          const critThresh = urgencyConfig.urgencyCriticalThreshold ?? 2;
          const lowMsg = urgencyConfig.checkoutLowMessage || 'Only {{spots}} spots left at this time';
          const critMsg = urgencyConfig.checkoutCriticalMessage || 'Only {{spots}} spots left \u2014 book now';
          const lastMsg = urgencyConfig.checkoutLastSpotMessage || 'This is the last available spot';
          const showPopular = urgencyConfig.popularEnabled !== false;

          if (showCheckoutUrgency && displayRemaining > 0 && displayRemaining <= lowThresh) {
            let msg = '';
            if (displayRemaining === 1) msg = lastMsg;
            else if (displayRemaining <= critThresh) msg = critMsg.replace('{{spots}}', String(displayRemaining));
            else msg = lowMsg.replace('{{spots}}', String(displayRemaining));
            return <div className="lr-urgency-text" data-testid="checkout-urgency">{'\u26A1'} {msg}</div>;
          }
          if (displayRemaining > lowThresh && showPopular && isPopular) {
            return <div className="lr-popular-text" data-testid="checkout-popular">{'\uD83D\uDD25'} One of our most popular time slots</div>;
          }
          return null;
        })()}

        {bookingError && <div className="lr-promo-error" data-testid="booking-error" style={{marginBottom:12}}>{bookingError}</div>}

        {/* What's Included */}
        <WhatsIncluded />

        <div className="lr-section">
          <div className="lr-section-title">{t('orderSummary')}</div>
          <div className="lr-summary-row"><span>{t('ticketLabel')} &times; {guests}</span><span>{fmt(base.subtotal)}</span></div>
          <div className="lr-summary-row"><span>{t('serviceFee')} ({config.serviceFeeRate}%)</span><span>{fmt(base.fee)}</span></div>
          <div className="lr-summary-row"><span>{t('tax')} ({config.taxRate}%)</span><span>{fmt(base.tax)}</span></div>
          {promoDiscount > 0 && <div className="lr-summary-row"><span>{t('promo')} ({appliedPromo.code})</span><span className="discount">-{fmt(promoDiscount)}</span></div>}
          <div className="lr-summary-row total"><span>{t('total')}</span><span>{fmt(finalTotal)}</span></div>
        </div>

        <div className="lr-section">
          <div className="lr-section-title">{t('contact')}</div>
          <div className="lr-field">
            <label className="lr-label">{t('fullName')}</label>
            <input className="lr-input" data-testid="contact-name" value={name} onChange={e => { setName(e.target.value); setAttempted(false); }} placeholder="Maria Santos" style={attempted && !name.trim() ? {borderColor:'var(--red)'} : {}} />
            {attempted && !name.trim() && <div className="lr-promo-error" data-testid="name-error" style={{marginTop:4}}>{t('nameRequired')}</div>}
          </div>
          <div className="lr-field">
            <label className="lr-label">{t('email')}</label>
            <input className="lr-input" data-testid="contact-email" type="email" value={email} onChange={e => { setEmail(e.target.value); setAttempted(false); }} placeholder="maria@example.com" style={attempted && !email.trim() ? {borderColor:'var(--red)'} : {}} />
            {attempted && !email.trim() && <div className="lr-promo-error" data-testid="email-error" style={{marginTop:4}}>{t('emailRequired')}</div>}
          </div>
          <div className="lr-field">
            <label className="lr-label">{t('phoneNumber')} <span style={{color:'var(--bark)',fontWeight:400}}>({t('optional')})</span></label>
            <input className="lr-input" data-testid="contact-phone" type="tel" value={phone} onChange={e => {
              const digits = e.target.value.replace(/\D/g, '');
              let formatted = '';
              if (digits.length === 0) formatted = '';
              else if (digits.length <= 1) formatted = '+' + digits;
              else if (digits.length <= 4) formatted = '+' + digits[0] + ' (' + digits.slice(1);
              else if (digits.length <= 7) formatted = '+' + digits[0] + ' (' + digits.slice(1, 4) + ') ' + digits.slice(4);
              else formatted = '+' + digits[0] + ' (' + digits.slice(1, 4) + ') ' + digits.slice(4, 7) + '-' + digits.slice(7, 11);
              setPhone(formatted);
            }} placeholder="+1 (787) 000-0000" />
          </div>
        </div>

        <div className="lr-section">
          <div className="lr-terms-row" data-testid="terms-checkbox-row">
            <label className="lr-terms-label">
              <input type="checkbox" className="lr-terms-check" data-testid="terms-checkbox" checked={termsAgreed} onChange={e => { setTermsAgreed(e.target.checked); if (e.target.checked) setTermsError(''); }} />
              <span className="lr-terms-text">
                {name.trim() ? <>{t('iPersonAgree', { name: name.trim() })} </> : <>{t('iAgree')} </>}
                <button type="button" className="lr-terms-link" data-testid="terms-link" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}>{t('termsAndConditions')}</button>
              </span>
            </label>
          </div>
          {termsError && <div className="lr-promo-error" data-testid="terms-error" style={{marginTop:6}}>{termsError}</div>}
        </div>

        {/* Cancellation Policy */}
        <div data-testid="cancellation-policy-box" style={{
          margin: '0 0 16px',
          padding: '14px 16px',
          background: '#FAFAF8',
          border: '1px solid #e8e4df',
          borderRadius: 8,
          fontSize: 12,
          lineHeight: 1.7,
          color: 'var(--bark)',
          fontFamily: 'Jost, sans-serif',
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('cancellationPolicy')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#2D7A4F' }}>&#10003;</span> {t('freeCancellation')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('noRefund24h')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('noRefundNoShow')}</div>
          <div style={{ fontSize: 11, color: '#8B7E74', marginTop: 6 }}>{t('stripeNonRefundable')}</div>
        </div>

        <div className="lr-section">
          <div className="lr-section-title">{t('promoCode')}</div>
          <div className="lr-promo-row">
            <input className="lr-input" data-testid="promo-input" value={promoInput}
              onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }} placeholder={t('enterCode')} />
            <button className="lr-btn-sm lr-btn-accent" data-testid="apply-promo" onClick={applyPromo}>{t('apply')}</button>
          </div>
          {appliedPromo && <div className="lr-promo-success" data-testid="promo-success">{'\u2713'} {appliedPromo.label} &mdash; {appliedPromo.type === 'percent' ? `${appliedPromo.discount}% ${t('off')}` : `${fmt(appliedPromo.discount)} ${t('off')}`}</div>}
          {promoError && <div className="lr-promo-error" data-testid="promo-error">{promoError}</div>}
        </div>

        <div className="lr-section">
          <div className="lr-section-title">{t('payment')}</div>
          <div ref={paymentRef} data-testid="stripe-payment-element" style={{minHeight:100,marginBottom:4}}></div>
          <div className="lr-stripe-badge" data-testid="stripe-secured-badge">&#128274; {t('paymentsSecuredBy')}</div>
          <button className="lr-pay-full primary" data-testid="pay-button" disabled={!paymentReady || paying || !termsAgreed} onClick={handlePay}>
            {paying ? t('processing') : `${t('pay')} ${fmt(finalTotal)}`}
          </button>
        </div>
      </div>
    </div>
    {showTermsModal && (
      <TermsModal
        onAgree={() => { setTermsAgreed(true); setTermsError(''); setShowTermsModal(false); }}
        onClose={() => setShowTermsModal(false)}
      />
    )}
  </>
  );
}
